import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { useUser } from '@itp/talent';

import { PRIVATE_API_URL } from '../constants/config';
import { requestAccessToken } from '../hooks/useFetch';
import { socketManager, useSocketManagerCleanup } from '../socket.io/socket-manager';
import { useLocalAuth } from './localAuthContext';

interface SocketContextType {
  socket: null | typeof socketManager;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<null | typeof socketManager>(null);
  const auth0 = useAuth0();
  const { token: localToken } = useLocalAuth();
  const { user } = useUser();
  const isConnectingRef = useRef(false);
  const isAdmin = !!user?.isAdmin;

  useSocketManagerCleanup();

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const initSocket = async () => {
      if (!user?.id) {
        return;
      }

      if (socketManager.isConnected() || isConnectingRef.current) {
        setSocket(socketManager);
        return;
      }

      isConnectingRef.current = true;

      try {
        let token: string;

        if (localToken) {
          token = localToken;
        } else {
          token = (await requestAccessToken(auth0)) as string;
        }

        await socketManager.init(PRIVATE_API_URL, token, {
          transports: ['websocket', 'polling'],
        });

        socketManager.on('connect', () => {
          isConnectingRef.current = false;
        });

        socketManager.on('disconnect', () => {
          isConnectingRef.current = false;
        });

        socketManager.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          isConnectingRef.current = false;
        });

        setSocket(socketManager);
      } catch (error) {
        console.error('Failed to initialize socket:', error);
        isConnectingRef.current = false;
      }
    };

    initSocket();

    return () => {
      socketManager.disconnect();
      isConnectingRef.current = false;
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0.isAuthenticated, localToken, user?.id, isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const handler = (e: Event) => {
      const token = (e as CustomEvent).detail?.token;
      if (token && socketManager.isConnected()) {
        socketManager.updateToken(token);
      }
    };

    window.addEventListener('local-token-updated', handler);
    return () => window.removeEventListener('local-token-updated', handler);
  }, [isAdmin]);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context.socket;
};
